import { useControllers } from "../state/controllers";
import { useProjectStore } from "../state/projectStore";
import { useEditorStore } from "../state/editorStore";
import type { EditCtx } from "../core/editor/noteOps";
import * as ops from "../core/editor/noteOps";
import type { NoteDuration, NoteEvent, Arrangement, TechniqueId } from "../types/domain";

function buildCtx(): EditCtx {
  const ps = useProjectStore.getState();
  const es = useEditorStore.getState();
  const notes: Record<number, NoteEvent[]> = {};
  const ts: Record<number, [number, number]> = {};
  for (const k in ps.arrangement.measures) {
    const mm = ps.arrangement.measures[+k];
    if (mm.notes) notes[+k] = mm.notes.map((n) => ({ ...n }));
    if (mm.timeSignature) ts[+k] = mm.timeSignature;
  }
  return {
    notes, ts, chartMeasures: ps.chartMeasures,
    editMode: es.editMode, activeMeasure: es.activeMeasure,
    selectedNoteIdx: es.selectedNoteIdx, insertArmed: es.insertArmed, curDur: es.curDur, curDotted: es.curDotted,
    curTriplet: es.curTriplet, curFx: es.curFx, curTie: es.curTie, curGrace: es.curGrace, curRestDotted: es.curRestDotted,
  };
}

export function useEditor() {
  const c = useControllers();

  // run a ctx-mutating op, commit notes+selection, and ensure the score re-styles even when the
  // notation string is unchanged (selection-only edits don't trigger a setTex re-render).
  const commit = (ctx: EditCtx) => {
    const ps = useProjectStore.getState();
    const before = ps.tex;
    const measures: Arrangement["measures"] = { ...ps.arrangement.measures };
    for (const k in ctx.notes) {
      const i = +k;
      measures[i] = { ...(measures[i] || {}), notes: ctx.notes[i] };
      if (ctx.ts[i]) measures[i].timeSignature = ctx.ts[i];
    }
    ps.setArrangement({ measures });
    useEditorStore.getState().set({
      activeMeasure: ctx.activeMeasure, selectedNoteIdx: ctx.selectedNoteIdx,
      insertArmed: ctx.insertArmed, curDur: ctx.curDur, curDotted: ctx.curDotted, curTriplet: ctx.curTriplet,
      curFx: ctx.curFx, curTie: ctx.curTie, curGrace: ctx.curGrace, curRestDotted: ctx.curRestDotted,
    });
    if (useProjectStore.getState().tex === before) c.score.restyle();   // tex unchanged → restyle now
  };

  const run = (fn: (ctx: EditCtx) => void) => { const ctx = buildCtx(); fn(ctx); commit(ctx); };

  // measure under the current playback position (so editing starts where you are)
  const measureAtCursor = (): number => {
    const ps = useProjectStore.getState();
    const t = c.activeSource().getPlaybackTime();
    let m = 0;
    for (let i = 0; i < ps.chartMeasures.length; i++) { if (t >= ps.chartMeasures[i].startTime - 0.01) m = i; else break; }
    return m;
  };

  return {
    enterEditMode() {
      useEditorStore.getState().set({ editMode: true });
      const ps = useProjectStore.getState();
      if (ps.chartMeasures.length) {
        // land on the beat that was clicked before entering edit mode; else the measure at the cursor
        const pend = useEditorStore.getState().pendingBeat;
        const m = pend ? pend.m : measureAtCursor();
        const b = pend ? pend.b : 0;
        run((ctx) => ops.materializeMeasure(ctx, m, b));
      } else c.score.restyle();
    },
    exitEditMode() {
      useEditorStore.getState().set({ editMode: false, activeMeasure: -1, selectedNoteIdx: -1, pendingBeat: null, rangeMode: false, rangeAnchor: null, rangeEnd: null });
      c.score.restyle();
    },
    selectMeasure(measureIndex: number, beatInBar: number) { run((ctx) => ops.materializeMeasure(ctx, measureIndex, beatInBar)); },
    tapFret(string: number, fret: number) {
      run((ctx) => {
        if (ctx.activeMeasure < 0) ops.materializeMeasure(ctx, 0, 0);   // auto-select 1st measure if none
        ops.addNote(ctx, string, fret);
      });
    },
    // add a muted/dead note (✕) on a string — same placement as tapFret, then mark it dead
    tapDead(string: number) {
      run((ctx) => {
        if (ctx.activeMeasure < 0) ops.materializeMeasure(ctx, 0, 0);
        ops.addDead(ctx, string);
      });
    },
    setMode(insertArmed: boolean) { useEditorStore.getState().set({ insertArmed, rangeMode: false, rangeAnchor: null, rangeEnd: null }); c.score.restyle(); },
    setDur(d: NoteDuration) { run((ctx) => ops.applyDur(ctx, d)); },
    // rest pen: remember the chosen rest length and convert/insert a rest of that length
    placeRest(d: NoteDuration) { useEditorStore.getState().set({ restDur: d }); run((ctx) => ops.addRest(ctx, d)); },
    deleteSelected() {
      const es = useEditorStore.getState();
      if (es.selectedNoteIdx < 0) return;
      run((ctx) => ops.deleteNote(ctx, es.selectedNoteIdx));
    },
    setFx(fx: TechniqueId) { run((ctx) => ops.setFx(ctx, fx)); },
    setTie() { run((ctx) => ops.setTie(ctx)); },
    setGrace(slide: boolean) { run((ctx) => ops.setGrace(ctx, slide)); },
    toggleDot() { run((ctx) => ops.toggleDot(ctx)); },
    toggleTriplet() { run((ctx) => ops.toggleTriplet(ctx)); },
    toggleRestDot() { run((ctx) => ops.toggleRestDot(ctx)); },
    setChord(label: string) { run((ctx) => ops.setChordLabel(ctx, label)); },

    // ---- 범위(range) mode + clipboard ----
    setRangeMode(on: boolean) {
      useEditorStore.getState().set({ rangeMode: on, rangeAnchor: null, rangeEnd: null });
      c.score.restyle();
    },
    // drag/tap start: begin a fresh range at this beat (global {m,b})
    rangeStart(measureIndex: number, beatInBar: number) {
      const p = { m: measureIndex, b: beatInBar };
      useEditorStore.getState().set({ activeMeasure: measureIndex, rangeAnchor: p, rangeEnd: p });
      c.score.restyle();
    },
    // drag move: extend the range end to the hovered beat (any measure — can cross into the next bar)
    rangeExtend(measureIndex: number, beatInBar: number) {
      const es = useEditorStore.getState();
      if (es.rangeAnchor == null) return;
      if (es.rangeEnd && es.rangeEnd.m === measureIndex && es.rangeEnd.b === beatInBar) return;
      useEditorStore.getState().set({ rangeEnd: { m: measureIndex, b: beatInBar } });
      c.score.restyle();
    },
    // drag a handle: move the lower ("start") or upper ("end") endpoint to a beat (any measure, clamped)
    setRangeBound(side: "start" | "end", measureIndex: number, beatInBar: number) {
      const es = useEditorStore.getState();
      if (es.rangeAnchor == null || es.rangeEnd == null) return;
      const cmp = (a: { m: number; b: number }, b: { m: number; b: number }) => a.m !== b.m ? a.m - b.m : a.b - b.b;
      const lo = cmp(es.rangeAnchor, es.rangeEnd) <= 0 ? es.rangeAnchor : es.rangeEnd;
      const hi = cmp(es.rangeAnchor, es.rangeEnd) <= 0 ? es.rangeEnd : es.rangeAnchor;
      const pos = { m: measureIndex, b: beatInBar };
      const nLo = side === "start" ? (cmp(pos, hi) <= 0 ? pos : hi) : lo;
      const nHi = side === "end" ? (cmp(pos, lo) >= 0 ? pos : lo) : hi;
      if (nLo.m === lo.m && nLo.b === lo.b && nHi.m === hi.m && nHi.b === hi.b) return;
      useEditorStore.getState().set({ rangeAnchor: nLo, rangeEnd: nHi });
      c.score.restyle();
    },
    copyRange() {
      const es = useEditorStore.getState();
      if (es.rangeAnchor == null || es.rangeEnd == null) return;
      let clip: NoteEvent[] = [];
      run((ctx) => { clip = ops.copyRangeGlobal(ctx, es.rangeAnchor!, es.rangeEnd!); });
      if (clip.length) useEditorStore.getState().set({ clipboard: clip });
    },
    cutRange() {
      const es = useEditorStore.getState();
      if (es.rangeAnchor == null || es.rangeEnd == null) return;
      run((ctx) => {
        const clip = ops.cutRangeGlobal(ctx, es.rangeAnchor!, es.rangeEnd!);
        if (clip.length) useEditorStore.getState().set({ clipboard: clip });
      });
      useEditorStore.getState().set({ rangeAnchor: null, rangeEnd: null });
    },
    paste() {
      const clip = useEditorStore.getState().clipboard;
      if (!clip || !clip.length) return;
      run((ctx) => ops.pasteOverwrite(ctx, clip));   // overwrite from the selected beat
    },

    resetScore() {
      useProjectStore.getState().setArrangement({ measures: {} });
      useEditorStore.getState().set({ activeMeasure: -1, selectedNoteIdx: -1 });
    },
    setBeatLevel(mul: 0.5 | 1 | 2) { useProjectStore.getState().setBeatLevelMul(mul); },
  };
}
